import { useMemo, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useReviewDescWidth, REVIEW_DESC_CSS_VAR } from '@/hooks/useReviewDescWidth';
import { LineItemStatusBadge } from '../LineItemStatusBadge';
import { LineItemInfoPopover } from '../LineItemInfoPopover';
import { EditableSpecCell } from '../EditableSpecCell';
import { useQuotationDraftStore } from '../../store/quotationDraftStore';
import { isSpecValidForRematch, revertPatch } from '../../utils/lineItem';
import { sourceNote } from '../../utils/cuttingListQuantity';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import {
  EDITABLE_DIAMETERS_MM,
  EDITABLE_LENGTHS_M,
  EDITABLE_GRADES,
  type LineItemMatchStatus,
  type QuotationDraft,
  type QuotationLineItemDto,
} from '../../types/quotation.types';

const DIAMETER_OPTIONS = EDITABLE_DIAMETERS_MM.map((d) => ({ value: String(d), label: `${d} mm` }));
const LENGTH_OPTIONS = EDITABLE_LENGTHS_M.map((l) => ({ value: String(l), label: `${l} m` }));
const GRADE_OPTIONS = EDITABLE_GRADES.map((g) => ({ value: g, label: `G${g}` }));
const WELDABLE_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

const STATUS_COUNT_ORDER: LineItemMatchStatus[] = [
  'MATCHED',
  'NEEDS_CLARIFICATION',
  'NO_MATCH',
  'SUMMARY_ONLY',
  'EXTERNAL_ATTACHMENT',
];

// 86d3fcbte — shared column geometry so the header row and the data rows stay
// pixel-aligned. The list is a horizontally-scrollable "table": the row number +
// description are pinned left (sticky) while the spec columns scroll. Spec-grid
// track widths fit the edited value + its ~28px EditableSpecCell overhead (px-1.5
// padding + gap-1 + the always-reserved chevron) AND the inline original-extraction
// hint shown *beside* an edited cell — so neither wraps. Description gets a fixed
// width (truncate + tooltip as a last resort) so the scroll columns line up across
// rows. Kept identical to the Sales Order review list.
const REVIEW_SPEC_GRID = 'grid grid-cols-[96px_100px_84px_52px_92px_48px] gap-x-1';
const REVIEW_NUM_COL = 'w-8 shrink-0';
const REVIEW_STATUS_COL = 'w-14 shrink-0';
const REVIEW_ACTION_COL = 'w-7 shrink-0';

// Small muted subtext that sits beneath an edited cell, showing the AI's
// original extraction value. Strikethrough was hard to read at small sizes —
// a plain smaller line works better. Diffs only render on cells that changed.
function OriginalHint({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="shrink-0 text-[9px] leading-none text-muted-foreground/70"
      title="Original extraction"
    >
      {children}
    </span>
  );
}

interface ReviewLineItemsListProps {
  draft: QuotationDraft;
}

// 86d318a0h: editable review-mode line items with inline per-cell diffs
// against the AI's original extraction. The original value sits as a small
// muted strikethrough next to each cell whose value the user has edited;
// unchanged cells render clean. Replaces an earlier "Show original" toggle
// — the inline diff is always visible, no mode switch required.
//
// Cells reuse EditableSpecCell (the same primitive QuotationLineItemsTable
// uses in compose mode) so the editing model is identical across surfaces.
// Per-row Revert remains in the existing LineItemInfoPopover (ⓘ icon).
// Required for rematch — gate on the server validator's rules (valid grade +
// positive diameter/length), not mere presence, so "Check all matches" never
// fires a request that 400s on an invalid spec (86d3dxnd7).
function canCheckMatch(li: QuotationLineItemDto): boolean {
  return isSpecValidForRematch(li);
}

export function ReviewLineItemsList({ draft }: ReviewLineItemsListProps) {
  const updateLineItem = useQuotationDraftStore((s) => s.updateLineItem);
  const rematchLineItem = useQuotationDraftStore((s) => s.rematchLineItem);
  const canEdit = useAuthStore((s) => s.hasPermission(PERMISSIONS.QUOTATIONS_CREATE_QUOTATION));
  const [isCheckingAll, setIsCheckingAll] = useState(false);
  const { width: descWidth, containerRef, startResize: startDescResize } = useReviewDescWidth();

  const lineItems = draft.lineItems;

  const statusCounts = useMemo(() => {
    const counts = new Map<LineItemMatchStatus, number>();
    for (const li of lineItems) {
      if (!li.matchStatus) continue;
      counts.set(li.matchStatus, (counts.get(li.matchStatus) ?? 0) + 1);
    }
    return STATUS_COUNT_ORDER.filter((s) => counts.has(s)).map((s) => ({
      status: s,
      count: counts.get(s)!,
    }));
  }, [lineItems]);

  // Same patch as QuotationLineItemsTable.revertOne (shared revertPatch): drops matched-item metadata
  // when the reverted spec is incomplete so the row goes back to "needs match".
  const revertOne = (item: QuotationLineItemDto) => {
    if (!item.originalSpec) return;
    updateLineItem(draft.id, item.id, revertPatch(item.originalSpec));
  };

  const isReadOnly = !canEdit;
  const matchableCount = useMemo(() => lineItems.filter(canCheckMatch).length, [lineItems]);
  const hasAnyMatching = lineItems.some((li) => li.isMatching);

  const handleCheckAllMatches = async () => {
    setIsCheckingAll(true);
    try {
      const ids = lineItems.filter(canCheckMatch).map((li) => li.id);
      await Promise.all(ids.map((id) => rematchLineItem(draft.id, id)));
    } finally {
      setIsCheckingAll(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="sticky top-0 z-10 border-b bg-background/95 px-3 py-2 backdrop-blur">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">
            Line Items <span className="text-muted-foreground">({lineItems.length})</span>
          </h2>
          {canEdit && matchableCount > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCheckAllMatches}
              disabled={isCheckingAll || hasAnyMatching}
              className="h-6 gap-1.5 px-2 text-[11px]"
              title={`Run match against NetSuite for ${matchableCount} line item${matchableCount === 1 ? '' : 's'} with complete specs`}
            >
              {isCheckingAll || hasAnyMatching
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <RefreshCw className="h-3 w-3" />}
              Check matches
            </Button>
          )}
        </div>
        {statusCounts.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {statusCounts.map(({ status, count }) => (
              <div key={status} className="flex items-center gap-0.5">
                <LineItemStatusBadge status={status} />
                <span className="text-[10px] tabular-nums text-muted-foreground">×{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 86d3fcbte — horizontally + vertically scrollable region. Rows are sized
          to content (w-max) so the spec columns can overflow and scroll; the
          row #/description group is pinned left (sticky) and the header <li> is
          pinned top, so the header corner stays put in both axes. */}
      <ol
        ref={containerRef}
        className="flex-1 divide-y overflow-auto"
        style={{ [REVIEW_DESC_CSS_VAR]: `${descWidth}px` } as React.CSSProperties}
      >
        {/* Sticky column header as the first <li> so it shares the scroll region
            (and thus the scrollbar-adjusted width) with the data rows. Mirrors
            the row's pinned-left + scrolling-right two-group layout exactly. */}
        <li className="sticky top-0 z-20 flex w-max min-w-full bg-muted text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {/* position:sticky also provides the containing block for the absolute
              resize handle below — no separate `relative` needed. */}
          <div className="sticky left-0 z-10 flex items-center gap-1.5 border-r bg-muted py-1 pl-3 pr-2">
            <span className={cn(REVIEW_NUM_COL, 'text-right')}>#</span>
            <span className="shrink-0" style={{ width: `var(${REVIEW_DESC_CSS_VAR})` }}>
              Description
            </span>
            {/* Drag handle — resize the pinned description column (persisted). */}
            <div
              role="separator"
              aria-orientation="vertical"
              onPointerDown={startDescResize}
              title="Drag to resize the description column"
              className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none hover:bg-primary/40 active:bg-primary"
            />
          </div>
          <div className="flex items-center gap-1.5 py-1 pl-2 pr-3">
            <div className={cn(REVIEW_SPEC_GRID, 'shrink-0')}>
              <span className="px-1.5">Ø</span>
              <span className="px-1.5">Length</span>
              <span className="px-1.5">Grade</span>
              <span className="px-1.5">W</span>
              <span className="px-1.5 text-right">Qty</span>
              <span className="px-1.5">Unit</span>
            </div>
            <span className={cn(REVIEW_STATUS_COL, 'text-center')}>Status</span>
            <span className={REVIEW_ACTION_COL} aria-hidden />
          </div>
        </li>
        {lineItems.length === 0 && (
          <li className="px-3 py-6 text-center text-xs text-muted-foreground">No line items.</li>
        )}
        {lineItems.map((li, idx) => {
          const sku = li.matchedSalesDescription ?? li.rawDescription ?? '—';
          const isMatched = !!li.matchedSalesDescription;
          const o = li.originalSpec;
          const showEditedMarker = li.isEdited && !!o;
          const canRevertRow = !!o;

          // Per-cell diff conditions: only render the strikethrough hint
          // when the current value differs from the original.
          const diffDiameter = o && o.diameterMm !== li.diameterMm;
          const diffLength = o && o.lengthM !== li.lengthM;
          const diffGrade = o && o.gradeCode !== li.gradeCode;
          const diffWeldable = o && o.isWeldable !== li.isWeldable;
          // The unit is part of the quantity: a converted line differs in unit even when the number matches.
          const diffQuantity = o && (o.quantity !== li.quantity || o.unit !== li.unit);
          const asked = sourceNote(li);

          return (
            <li
              key={li.id}
              className="flex w-max min-w-full text-[11px]"
              title={showEditedMarker ? 'Edited from extraction' : undefined}
            >
              {/* Pinned-left group: row # + full description stay visible while the
                  spec columns scroll horizontally. The edited marker rides here so
                  it's never scrolled out of view. */}
              <div
                className={cn(
                  'sticky left-0 z-10 flex items-center gap-1.5 border-r bg-background py-1 pl-3 pr-2',
                  // pl-2.5 (10px) + border-l-2 (2px) = pl-3 (12px), so the edit
                  // marker doesn't shift the row out of alignment with the others.
                  showEditedMarker && 'border-l-2 border-l-warning pl-2.5',
                )}
              >
                <span
                  className={cn(
                    REVIEW_NUM_COL,
                    'text-right font-mono tabular-nums text-muted-foreground',
                  )}
                >
                  #{idx + 1}
                </span>
                <p
                  className={cn(
                    'shrink-0 truncate text-xs',
                    isMatched ? 'font-medium text-foreground' : 'italic text-muted-foreground',
                  )}
                  style={{ width: `var(${REVIEW_DESC_CSS_VAR})` }}
                  title={sku}
                >
                  {sku}
                </p>
              </div>
              {/* Scrolling group: spec cells (fixed-track grid so columns align
                  across rows) + status + action. */}
              <div className="flex items-center gap-1.5 py-1 pl-2 pr-3">
                <div className={cn(REVIEW_SPEC_GRID, 'shrink-0 items-center text-muted-foreground')}>
                <div className="flex items-center gap-1">
                  <EditableSpecCell
                    kind="select"
                    value={li.diameterMm != null ? String(li.diameterMm) : null}
                    options={DIAMETER_OPTIONS}
                    disabled={isReadOnly}
                    ariaLabel="Diameter (mm)"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) =>
                      updateLineItem(draft.id, li.id, {
                        diameterMm: v != null ? Number(v) : null,
                      })
                    }
                  />
                  {diffDiameter && (
                    <OriginalHint>{o!.diameterMm != null ? `${o!.diameterMm} mm` : '—'}</OriginalHint>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <EditableSpecCell
                    kind="select"
                    value={li.lengthM != null ? String(li.lengthM) : null}
                    options={LENGTH_OPTIONS}
                    disabled={isReadOnly}
                    ariaLabel="Length (m)"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) =>
                      updateLineItem(draft.id, li.id, {
                        lengthM: v != null ? Number(v) : null,
                      })
                    }
                  />
                  {diffLength && (
                    <OriginalHint>{o!.lengthM != null ? `${o!.lengthM} m` : '—'}</OriginalHint>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <EditableSpecCell
                    kind="select"
                    value={li.gradeCode}
                    options={GRADE_OPTIONS}
                    disabled={isReadOnly}
                    ariaLabel="Grade"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) => updateLineItem(draft.id, li.id, { gradeCode: v })}
                  />
                  {diffGrade && (
                    <OriginalHint>{o!.gradeCode != null ? `G${o!.gradeCode}` : '—'}</OriginalHint>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <EditableSpecCell
                    kind="select"
                    value={li.isWeldable ? 'Y' : 'N'}
                    options={WELDABLE_OPTIONS}
                    disabled={isReadOnly}
                    ariaLabel="Weldable"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) => updateLineItem(draft.id, li.id, { isWeldable: v === 'Y' })}
                  />
                  {diffWeldable && (
                    <OriginalHint>{o!.isWeldable ? 'Y' : 'N'}</OriginalHint>
                  )}
                </div>
                {/* Native title (never a Radix Tooltip — this repeats per row): the customer's own value when
                    the cutting-list rule rounded or converted the line (14ykpqtw3hw, Requirement 6). */}
                <div className="flex items-center gap-1" title={asked?.title}>
                  <EditableSpecCell
                    kind="number"
                    value={li.quantity ?? null}
                    min={1}
                    step={1}
                    disabled={isReadOnly}
                    ariaLabel="Quantity"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) => updateLineItem(draft.id, li.id, { quantity: v })}
                  />
                  {diffQuantity && (
                    <OriginalHint>{o!.quantity != null ? `${o!.quantity} ${o!.unit}` : '—'}</OriginalHint>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {/* Read-only (86d3dxnd7). A kg order with no exact item stays kg (QUO-R-003e). */}
                  <div className="px-1.5 py-0 text-[11px] text-foreground">{li.unit}</div>
                </div>
              </div>
                <div className={cn(REVIEW_STATUS_COL, 'flex justify-center')}>
                  {li.matchStatus && <LineItemStatusBadge status={li.matchStatus} compact />}
                </div>
                <div className={cn(REVIEW_ACTION_COL, 'flex justify-center')}>
                  <LineItemInfoPopover
                    item={li}
                    onRevert={canRevertRow ? () => revertOne(li) : undefined}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
